import React from 'react';
import { View, Text, Link } from '@react-pdf/renderer';
import type { ModuleDefinition } from './moduleRegistry';
import type { ReportData } from '../tokens/types';
import { PdfSection, PdfSparkBars } from '../primitives/PdfPrimitives';
import { ScreenSection, ScreenSparkBars } from '../primitives/ScreenPrimitives';
import { PdfLineChart, ScreenLineChart, type LineChartPoint } from '../primitives/ChartPrimitives';
import { overviewColors, overviewDesign, newsDesign, competitorsDesign, coverFonts, ptToPx, interSectionGap } from '../tokens/designTokens';
import { fmtMonthYear, fmtMonthYearShort } from './formatUtils';
import {
    stripHtml,
    truncateText,
    sourceInitials,
    scoreToBand,
    bandLabel,
    scoreBarFraction,
    sentimentTone,
    type RiskBand,
} from '../tokens/overviewHelpers';

// ─── Color resolver ─────────────────────────────────────────────
// Tokens reference colour names (string keys). resolve() turns them into hex.
type ColorKey = keyof typeof overviewColors;
const c = (key: string): string => (overviewColors as Record<string, string>)[key] ?? key;

// ─── Band → colour ──────────────────────────────────────────────
const bandColor = (band: RiskBand): string =>
    band === 'LOW'
        ? overviewColors.pos
        : band === 'MEDIUM'
        ? overviewColors.warn
        : band === 'HIGH'
        ? overviewColors.neg
        : overviewColors.sev;

// ─── View model ─────────────────────────────────────────────────
type ChipTone = 'positive' | 'accent' | 'muted';
type Chip = { label: string; tone: ChipTone };
type SectorMetric = { label: string; value: string; unit?: string; sub?: string; valueColor?: string };

type OverviewVM = {
    initial: string;
    orgTitle: string;
    chips: Chip[];
    subtitle: string;
    aboutText?: string;
    businessCards: Array<{ title: string; body: string }>;
    basicDetails: {
        items: Array<{ label: string; value: string; href?: string }>;
        address?: string;
    };
    riskGrade?: {
        band: RiskBand;
        color: string;
        fraction: number;
        description: string;
        pillars: Array<{ label: string; band: RiskBand; color: string }>;
    };
    news: Array<{
        url?: string;
        source: string;
        initials: string;
        sentiment: 'pos' | 'neg' | 'neutral';
        sentimentLabel: string;
        headline: string;
        date: string;
    }>;
    sectorOutlook?: {
        sectorName?: string;
        intro?: string;
        metrics: SectorMetric[];
        body?: string;
        growthDrivers: string[];
        keyRisks: string[];
    };
    competitors?: {
        focal: { name: string };
        rivals: Array<{ name: string }>;
        rows: Array<{ key: string; label: string; values: string[]; mono?: boolean }>;
        cards: Array<{
            name: string;
            kind: 'subject' | 'competitor';
            description?: string;
            foundingYear?: string;
            location?: string;
        }>;
    };
};

const RISK_DESCRIPTION =
    'Indicates creditworthiness, calculated by aggregating scores across Financial, Management, Governance, and Legal and Regulatory checks.';

const buildVM = (data: ReportData): OverviewVM => {
    const o = data.overview;
    const id = data.identity;
    const score = data.score;

    const initial = (id.orgName ?? '?').trim()[0]?.toUpperCase() ?? '?';

    const chips: Chip[] = [];
    if (id.profileStatus) chips.push({ label: id.profileStatus, tone: 'positive' });
    if (id.startupSme) chips.push({ label: id.startupSme, tone: 'accent' });

    const subtitleParts: string[] = [];
    if (id.foundationDate) subtitleParts.push(`Founded ${fmtMonthYear(id.foundationDate)}`);
    if (id.sector) subtitleParts.push(id.sector);
    if (id.state) subtitleParts.push(id.state);
    const subtitle = subtitleParts.join(' · ');

    // Per PM feedback: render full about + business-card text (no truncation).
    // Text wraps naturally and the section height grows to fit. The
    // overviewDesign.aboutMaxChars / bpBodyMaxChars tokens remain for emergency
    // re-application but aren't called here.
    const aboutText = o?.overviewContent ? stripHtml(o.overviewContent) : undefined;

    const businessCards: OverviewVM['businessCards'] = [];
    if (o?.productOfferings) businessCards.push({ title: 'Product & Offerings', body: stripHtml(o.productOfferings) });
    if (o?.customersAndSalesChannels) businessCards.push({ title: 'Customers & Channels', body: stripHtml(o.customersAndSalesChannels) });
    if (o?.geographiesSegmentation) businessCards.push({ title: 'Geographies', body: stripHtml(o.geographiesSegmentation) });

    // Basic Details. `href` (optional per row) is rendered as a clickable
    // hyperlink in the PDF — used for the company website. Static rows like
    // PAN / CIN / GSTIN don't get a href so they render as plain mono text.
    const bdItems: Array<{ label: string; value: string; href?: string }> = [];
    if (id.pan && id.pan !== '—') bdItems.push({ label: 'PAN', value: id.pan });
    if (id.cin && id.cin !== '—') bdItems.push({ label: 'CIN', value: id.cin });
    const primaryGstin = id.gstins?.[0]?.gstin;
    if (primaryGstin) bdItems.push({ label: 'GSTIN', value: primaryGstin });
    if (id.websiteUrl) {
        const display = id.websiteUrl.replace(/^https?:\/\//i, '').replace(/\/$/, '');
        bdItems.push({ label: 'Website', value: display, href: id.websiteUrl });
    }
    const basicDetails = { items: bdItems, address: id.fullAddress };

    // Risk Grade
    let riskGrade: OverviewVM['riskGrade'];
    const band = scoreToBand(score?.companyScore ?? null);
    if (band) {
        const pillars = (score?.headers ?? [])
            .map((h) => {
                const b = scoreToBand(h.headerScore);
                if (!b) return null;
                return { label: h.displayName, band: b, color: bandColor(b) };
            })
            .filter((x): x is NonNullable<typeof x> => !!x)
            .slice(0, 5);
        riskGrade = {
            band,
            color: bandColor(band),
            fraction: scoreBarFraction(score?.companyScore),
            description: RISK_DESCRIPTION,
            pillars,
        };
    }

    // News
    const news = (o?.news ?? []).slice(0, 4).map((n) => ({
        url: n.redirectUrl,
        source: truncateText(n.publisherName, 22),
        initials: sourceInitials(n.publisherName),
        sentiment: sentimentTone(n.sentiment),
        sentimentLabel: (n.sentiment ?? '').toUpperCase() || 'NEUTRAL',
        headline: truncateText(stripHtml(n.title), 90),
        date: fmtMonthYear(n.publishedDate) || n.publishedDate,
    }));

    // Sector outlook
    let sectorOutlook: OverviewVM['sectorOutlook'];
    if (o?.sectorOverview || o?.marketSize || o?.industryCagr || o?.marketGrowth || o?.sectorRisks) {
        const metrics: SectorMetric[] = [];
        if (o.marketSize) {
            const parsed = parseMetric(o.marketSize);
            metrics.push({ label: 'Market Size', ...parsed });
        }
        if (o.industryCagr) {
            const parsed = parseMetric(o.industryCagr);
            metrics.push({ label: 'Industry CAGR', ...parsed });
        }
        if (o.sectorRisks) {
            metrics.push({ label: 'Sector Risk', value: capFirst(o.sectorRisks), valueColor: riskValueColor(o.sectorRisks) });
        }
        if (o.marketGrowth) {
            const parsed = parseMetric(o.marketGrowth);
            metrics.push({ label: 'Market Growth', ...parsed });
        }
        sectorOutlook = {
            sectorName: id.sector,
            intro: o.sectoralPerformance ? truncateText(stripHtml(o.sectoralPerformance), 220) : undefined,
            metrics,
            body: o.sectorOverview ? truncateText(stripHtml(o.sectorOverview), 480) : undefined,
            growthDrivers: (o.growthDrivers ?? []).map(stripHtml).filter(Boolean).slice(0, 4),
            keyRisks: (o.keyRisks ?? []).map(stripHtml).filter(Boolean).slice(0, 4),
        };
    }

    // Competitors
    let competitors: OverviewVM['competitors'];
    const allCompetitors = o?.competitors ?? [];
    if (allCompetitors.length > 0) {
        const focal = { name: id.orgName };
        const rivals = allCompetitors.slice(0, 3).map((cm) => ({ name: cm.companyName }));
        const focalCompetitor = {
            companyName: id.orgName,
            description: aboutText,
            location: id.state,
            revenue: undefined as string | undefined,
            employees: data.management?.employeeRange,
            foundingYear: id.foundationDate ? new Date(id.foundationDate).getFullYear().toString() : undefined,
            totalFunding: o?.fundingRaised,
            fundingStage: id.startupSme,
            investors: (o?.keyInvestors ?? []).slice(0, 3).join(', '),
        };
        const cols = [focalCompetitor, ...allCompetitors.slice(0, 3)];
        const rowSpec: Array<{ key: string; label: string; mono?: boolean; pick: (cm: any) => string | undefined }> = [
            { key: 'about', label: 'About', pick: (cm) => cm.description },
            { key: 'location', label: 'Location', pick: (cm) => cm.location },
            { key: 'revenue', label: 'Revenue', mono: true, pick: (cm) => cm.revenue },
            { key: 'employees', label: 'Employees', mono: true, pick: (cm) => cm.employees },
            { key: 'founded', label: 'Founded', mono: true, pick: (cm) => cm.foundingYear },
            { key: 'funding', label: 'Funding', mono: true, pick: (cm) => cm.totalFunding },
            // 'Stage' / 'Funding Stage' row removed per design feedback —
            // the value (id.startupSme) is rarely populated and read as
            // noise next to the other rows. Re-add the row spec entry to
            // surface it again.
            { key: 'investors', label: 'Investors', pick: (cm) => cm.investors },
        ];
        const rows = rowSpec
            .map((r) => ({
                key: r.key,
                label: r.label,
                mono: r.mono,
                values: cols.map((cm) => {
                    const v = r.pick(cm);
                    if (!v || v === '—') return 'Not disclosed';
                    return r.key === 'about' ? truncateText(v, 140) : v;
                }),
            }))
            .filter((r) => r.values.some((v) => v !== 'Not disclosed'));
        const cards = cols.map((cm, i) => ({
            // Per PM feedback: stop truncating mid-name. Render the raw value
            // and let the Text component wrap. The card's name/description
            // minHeight reservations only set a *minimum* — the View grows to
            // fit any longer content. The truncate token caps remain in
            // designTokens but are no longer applied at the VM layer.
            name: cm.companyName ?? '—',
            kind: (i === 0 ? 'subject' : 'competitor') as 'subject' | 'competitor',
            description: cm.description,
            foundingYear: cm.foundingYear,
            location: cm.location,
        }));
        competitors = { focal, rivals, rows, cards };
    }

    return {
        initial,
        orgTitle: id.orgName ?? '—',
        chips,
        subtitle,
        aboutText,
        businessCards,
        basicDetails,
        riskGrade,
        news,
        sectorOutlook,
        competitors,
    };
};

// Parse a sector-outlook metric value into its three visual parts:
//   - `value`: the headline number (mono medium, primary ink)
//   - `unit`:  the currency / scale (mono regular, muted ink, sits next to value)
//   - `sub`:   trailing context like "by 2028" or "(US$28 Bn)" — separate line, smaller
//
// Examples:
//   "Rs. 2,43,236 Cr (US$28 Bn)" → { value: 'Rs. 2,43,236', unit: 'Cr', sub: 'US$28 Bn' }
//   "₹ 5,179 Cr by 2028"         → { value: '₹ 5,179',      unit: 'Cr', sub: 'by 2028' }
//   "10-11% annually"            → { value: '10-11%',       sub: 'annually' }
//   "15%"                        → { value: '15%' }
// Replace currency / scale glyphs that GeistMono (the value font) lacks. When a
// font has no glyph for a code point, react-pdf either drops it to zero advance
// width (next char overlaps) or substitutes a tofu — either way the metric reads
// as garbage like "T9 billion" instead of "₹9 billion". Mono fonts in particular
// rarely ship Indian currency / extended Latin glyphs, so we ASCII-fy on the way
// in. Keep this list narrow; only add a symbol here after seeing it actually
// fail to render.
const sanitizeForMonoFont = (s: string): string =>
    s
        .replace(/₹\s*/g, 'Rs. ')
        .replace(/\s{2,}/g, ' ')
        .trim();

const parseMetric = (raw: string): { value: string; unit?: string; sub?: string } => {
    const trimmed = sanitizeForMonoFont(raw);

    // 1. Pull off any trailing parenthetical — that's always sub.
    const parenMatch = trimmed.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
    let main = trimmed;
    let sub: string | undefined;
    if (parenMatch) {
        main = parenMatch[1].trim();
        sub = parenMatch[2].trim();
    }

    // 2. Split off "VALUE UNIT [TRAILING SUB]" where unit is one of the known scale words.
    const unitMatch = main.match(/^(.+?)\s+(Cr|Crore|Lakh|Mn|Bn|Million|Billion)(?:\s+(.+))?$/i);
    if (unitMatch) {
        const value = unitMatch[1].trim();
        const unit = unitMatch[2];
        const trailing = unitMatch[3]?.trim();
        return { value, unit, sub: sub ?? trailing };
    }

    // 3. Percent at the end of the value, followed by trailing sub: "10-11% annually".
    const pctMatch = main.match(/^([\d.,\-\s]+%)\s+(.+)$/);
    if (pctMatch) {
        return { value: pctMatch[1].trim(), sub: sub ?? pctMatch[2].trim() };
    }

    return { value: main, sub };
};

const capFirst = (s: string): string => (s ? s[0].toUpperCase() + s.slice(1).toLowerCase() : s);

const riskValueColor = (s: string): string => {
    const v = s.toLowerCase();
    if (v.includes('high') || v.includes('severe')) return overviewColors.neg;
    if (v.includes('medium')) return overviewColors.warn;
    return overviewColors.pos;
};

// ════════════════════════════════════════════════════════════════
//                            P D F
// ════════════════════════════════════════════════════════════════

const PF = coverFonts.pdf;
const od = overviewDesign;
const nd = newsDesign;
const cd = competitorsDesign;

const PdfH3: React.FC<{ children: React.ReactNode; topGap?: number }> = ({ children, topGap = od.sectionGap }) => (
    <Text
        style={{
            fontSize: od.h3Size,
            fontFamily: PF.sans,
            fontWeight: 600,
            color: c(od.h3Color),
            marginTop: topGap,
            marginBottom: od.headingToBody,
        }}
    >
        {children}
    </Text>
);

const PdfChipBox: React.FC<{ label: string; tone: ChipTone }> = ({ label, tone }) => {
    const tones: Record<ChipTone, { bg: string; fg: string }> = {
        positive: { bg: overviewColors.posSoft, fg: overviewColors.pos },
        accent: { bg: overviewColors.accentSoft, fg: overviewColors.accentDeep },
        muted: { bg: overviewColors.surfaceAlt, fg: overviewColors.inkSubtle },
    };
    const t = tones[tone];
    // Three-part centering pattern (skill rule 9): lineHeight:1 on Text +
    // alignItems/justifyContent on View + asymmetric padding. Without all three,
    // react-pdf's baseline lands low and the chip text reads "too low".
    return (
        <View
            style={{
                backgroundColor: t.bg,
                paddingHorizontal: od.idChipPadX,
                paddingTop: 2,
                paddingBottom: 3,
                borderRadius: od.idChipRadius,
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Text
                style={{
                    fontSize: od.idChipSize,
                    fontFamily: PF.sans,
                    fontWeight: 600,
                    color: t.fg,
                    letterSpacing: od.idChipTrackingEm * od.idChipSize,
                    textTransform: 'uppercase',
                    lineHeight: 1,
                }}
            >
                {label}
            </Text>
        </View>
    );
};

const PdfIdentityCard: React.FC<{ vm: OverviewVM }> = ({ vm }) => (
    <View
        style={{
            backgroundColor: overviewColors.surface,
            borderRadius: od.idCardRadius,
            paddingHorizontal: od.idCardPadX,
            paddingVertical: od.idCardPadY,
            flexDirection: 'row',
            alignItems: 'center',
            gap: od.idCardGap,
        }}
        wrap={false}
    >
        <View
            style={{
                width: od.idLogoSize,
                height: od.idLogoSize,
                borderRadius: od.idLogoRadius,
                backgroundColor: overviewColors.ink,
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Text style={{ fontSize: od.idLogoTextSize, color: '#FFFFFF', fontFamily: PF.display, fontWeight: 400, lineHeight: 1 }}>
                {vm.initial}
            </Text>
        </View>
        <View style={{ flex: 1 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: od.idChipGap, flexWrap: 'wrap' }}>
                <Text
                    style={{
                        fontSize: od.idTitleSize,
                        fontFamily: PF.display,
                        fontWeight: 400,
                        color: overviewColors.ink,
                        lineHeight: od.idTitleLineHeight,
                    }}
                >
                    {vm.orgTitle}
                </Text>
                {vm.chips.map((ch) => (
                    <PdfChipBox key={ch.label} label={ch.label} tone={ch.tone} />
                ))}
            </View>
            {vm.subtitle ? (
                <Text
                    style={{
                        fontSize: od.idSubtitleSize,
                        fontFamily: PF.sans,
                        color: c(od.idSubtitleColor),
                        marginTop: 3,
                        lineHeight: od.idSubtitleLineHeight,
                    }}
                >
                    {vm.subtitle}
                </Text>
            ) : null}
        </View>
    </View>
);

const PdfBusinessCard: React.FC<{ title: string; body: string }> = ({ title, body }) => (
    <View
        style={{
            flex: 1,
            backgroundColor: '#FFFFFF',
            borderWidth: od.bpCardBorder,
            borderColor: overviewColors.border,
            borderRadius: od.bpCardRadius,
            padding: od.bpCardPad,
        }}
    >
        <Text
            style={{
                fontSize: od.bpTitleSize,
                fontFamily: PF.sans,
                fontWeight: 600,
                color: c(od.bpTitleColor),
                marginBottom: 5,
            }}
        >
            {title}
        </Text>
        <Text
            style={{
                fontSize: od.bpBodySize,
                fontFamily: PF.sans,
                color: c(od.bpBodyColor),
                lineHeight: od.bpBodyLineHeight,
            }}
        >
            {body}
        </Text>
    </View>
);

const PdfBasicDetails: React.FC<{ vm: OverviewVM['basicDetails'] }> = ({ vm }) => (
    <View
        style={{
            backgroundColor: '#FFFFFF',
            borderWidth: od.bdCardBorder,
            borderColor: overviewColors.border,
            borderRadius: od.bdCardRadius,
            padding: od.bdCardPad,
        }}
        wrap={false}
    >
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', columnGap: od.bdItemGap, rowGap: 4 }}>
            {vm.items.map((it) => (
                <View key={it.label} style={{ flexDirection: 'row', alignItems: 'baseline', gap: 2.5 }}>
                    <Text
                        style={{
                            fontSize: od.bdLabelSize,
                            fontFamily: PF.sans,
                            fontWeight: 500,
                            color: c(od.bdLabelColor),
                            letterSpacing: od.bdLabelTrackingEm * od.bdLabelSize,
                            textTransform: 'uppercase',
                        }}
                    >
                        {it.label}:
                    </Text>
                    {it.href ? (
                        <Link
                            src={it.href}
                            style={{
                                fontSize: od.bdValueSize,
                                fontFamily: PF.mono,
                                fontWeight: 500,
                                color: overviewColors.accent,
                                textDecoration: 'none',
                            }}
                        >
                            {it.value}
                        </Link>
                    ) : (
                        <Text style={{ fontSize: od.bdValueSize, fontFamily: PF.mono, fontWeight: 500, color: c(od.bdValueColor) }}>
                            {it.value}
                        </Text>
                    )}
                </View>
            ))}
        </View>
        {vm.address ? (
            <Text
                style={{
                    fontSize: od.bdAddressSize,
                    fontFamily: PF.sans,
                    color: c(od.bdAddressColor),
                    marginTop: od.bdRowGap,
                    lineHeight: od.bdAddressLineHeight,
                }}
            >
                {vm.address}
            </Text>
        ) : null}
    </View>
);

const PdfRiskGrade: React.FC<{ vm: NonNullable<OverviewVM['riskGrade']> }> = ({ vm }) => (
    <View style={{ flexDirection: 'row', gap: od.bpGap }} wrap={false}>
        <View
            style={{
                flex: 1,
                backgroundColor: '#FFFFFF',
                borderWidth: od.rgCardBorder,
                borderColor: overviewColors.border,
                borderRadius: od.rgCardRadius,
                padding: od.rgCardPad,
                flexDirection: 'row',
                alignItems: 'center',
                gap: od.rgGap,
            }}
        >
            <View style={{ alignItems: 'center' }}>
                <Text
                    style={{
                        fontSize: od.rgBadgeSize,
                        fontFamily: PF.mono,
                        fontWeight: 500,
                        color: vm.color,
                        letterSpacing: od.rgBadgeTrackingEm * od.rgBadgeSize,
                        // Tight line-height + extra bottom space prevents the descender bowl
                        // from overlapping the bar that sits below.
                        lineHeight: 1.1,
                        marginBottom: 6,
                    }}
                >
                    {bandLabel(vm.band)}
                </Text>
                <View
                    style={{
                        width: od.rgBarWidth,
                        height: od.rgBarHeight,
                        backgroundColor: overviewColors.surfaceAlt,
                        borderRadius: od.rgBarRadius,
                    }}
                >
                    <View
                        style={{
                            width: od.rgBarWidth * vm.fraction,
                            height: od.rgBarHeight,
                            backgroundColor: vm.color,
                            borderRadius: od.rgBarRadius,
                        }}
                    />
                </View>
            </View>
            <Text
                style={{
                    flex: 1,
                    fontSize: od.rgDescSize,
                    fontFamily: PF.sans,
                    color: c(od.rgDescColor),
                    lineHeight: od.rgDescLineHeight,
                }}
            >
                {vm.description}
            </Text>
        </View>
        {vm.pillars.length > 0 ? (
            <View
                style={{
                    flex: 1,
                    backgroundColor: '#FFFFFF',
                    borderWidth: od.rgCardBorder,
                    borderColor: overviewColors.border,
                    borderRadius: od.rgCardRadius,
                    paddingHorizontal: od.rgCardPad,
                    paddingVertical: od.rgCardPad - 4,
                    justifyContent: 'center',
                }}
            >
                {vm.pillars.map((p) => (
                    <View
                        key={p.label}
                        style={{
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            minHeight: od.rgPillarRowHeight,
                        }}
                    >
                        <Text style={{ fontSize: od.rgPillarLabelSize, color: c(od.rgPillarLabelColor), fontFamily: PF.sans }}>
                            {p.label}
                        </Text>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                            <View
                                style={{
                                    width: od.rgPillarDotSize,
                                    height: od.rgPillarDotSize,
                                    borderRadius: od.rgPillarDotSize / 2,
                                    backgroundColor: p.color,
                                }}
                            />
                            <Text
                                style={{
                                    fontSize: od.rgPillarBandSize,
                                    fontFamily: PF.sans,
                                    fontWeight: 600,
                                    color: p.color,
                                    letterSpacing: 0.5,
                                    textTransform: 'uppercase',
                                }}
                            >
                                {bandLabel(p.band)}
                            </Text>
                        </View>
                    </View>
                ))}
            </View>
        ) : null}
    </View>
);

const PdfNewsCard: React.FC<{ item: OverviewVM['news'][number] }> = ({ item }) => {
    const sentColors = {
        pos: { bg: overviewColors.posSoft, fg: overviewColors.pos },
        neg: { bg: overviewColors.negSoft, fg: overviewColors.neg },
        neutral: { bg: overviewColors.surfaceAlt, fg: overviewColors.inkSubtle },
    }[item.sentiment];
    return (
        // No `flex: 1` on the card — the parent column wrapper (`<View flex:1>`)
        // already owns the column width. Doubling flex on the card was causing
        // intermittent zero-width renders in @react-pdf/renderer.
        <View
            style={{
                backgroundColor: '#FFFFFF',
                borderWidth: nd.cardBorder,
                borderColor: overviewColors.border,
                borderRadius: nd.cardRadius,
                padding: nd.cardPad,
            }}
            wrap={false}
        >
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: nd.sourceGap, flex: 1 }}>
                    <View
                        style={{
                            width: nd.sourcePillSize,
                            height: nd.sourcePillSize,
                            borderRadius: nd.sourcePillRadius,
                            backgroundColor: c(nd.sourcePillBg),
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <Text
                            style={{
                                fontSize: nd.sourcePillTextSize,
                                color: c(nd.sourcePillTextColor),
                                fontFamily: PF.sans,
                                fontWeight: 600,
                                // 1.05 (not 1.0) — at lineHeight 1, react-pdf occasionally
                                // pushes 1-char initials below the pill's vertical center.
                                lineHeight: 1.05,
                            }}
                        >
                            {item.initials}
                        </Text>
                    </View>
                    {/* Wrap the source name in a flex:1 View, not a flex:1 Text.
                        `flex: 1` directly on Text in react-pdf can let an unwrapped
                        long string spill outside the card and overlap the headline
                        below — visible as a stray character behind the body text. */}
                    <View style={{ flex: 1 }}>
                        <Text style={{ fontSize: nd.sourceLabelSize, color: c(nd.sourceLabelColor), fontFamily: PF.sans, fontWeight: 600 }}>
                            {item.source}
                        </Text>
                    </View>
                </View>
                {/* Sentiment chip. Centering text inside a chip in @react-pdf/renderer
                    is a known trap: the text baseline lands toward the bottom of the
                    line box, so a chip with symmetric padding looks "text too low".
                    The fix is THREE coordinated things — change one without the
                    others and the text drifts again:
                      1. `lineHeight: 1` on the Text — collapses the line box so it
                         hugs the glyph, removing the extra ascender space above.
                      2. `alignItems: 'center', justifyContent: 'center'` on the View
                         — tells the View to center the (now glyph-sized) Text box.
                      3. Asymmetric padding (top < bottom by ~1pt) — counteracts the
                         residual baseline offset that lineHeight:1 doesn't fully
                         remove. paddingTop:2 / paddingBottom:3 looks centered. */}
                <View
                    style={{
                        backgroundColor: sentColors.bg,
                        paddingHorizontal: nd.sentChipPadX,
                        paddingTop: 2,
                        paddingBottom: 3,
                        borderRadius: nd.sentChipRadius,
                        flexShrink: 0,
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Text
                        style={{
                            fontSize: nd.sentChipSize,
                            fontFamily: PF.sans,
                            fontWeight: 600,
                            color: sentColors.fg,
                            letterSpacing: nd.sentChipTrackingEm * nd.sentChipSize,
                            textTransform: 'uppercase',
                            lineHeight: 1,
                        }}
                    >
                        {item.sentimentLabel}
                    </Text>
                </View>
            </View>
            {/* Reserve 2 headline lines so the 2x2 grid equalizes regardless of
                whether a given headline wraps to one or two lines. Wrap text in a
                View because marginTop on a wrapping Text can collapse in react-pdf.
                When a redirectUrl is available the headline is wrapped in
                <Link> — the PDF reader treats it as a clickable hyperlink to
                the original article. Brand colour + underline so readers can
                see at a glance that the headline is clickable. */}
            <View style={{ marginTop: 6, minHeight: nd.headlineSize * nd.headlineLineHeight * 2 }}>
                {item.url ? (
                    <Link
                        src={item.url}
                        style={{
                            fontSize: nd.headlineSize,
                            fontFamily: PF.sans,
                            fontWeight: 500,
                            color: '#6021B3',
                            lineHeight: nd.headlineLineHeight,
                            textDecoration: 'underline',
                        }}
                    >
                        {item.headline}
                    </Link>
                ) : (
                    <Text
                        style={{
                            fontSize: nd.headlineSize,
                            fontFamily: PF.sans,
                            fontWeight: 500,
                            color: c(nd.headlineColor),
                            lineHeight: nd.headlineLineHeight,
                        }}
                    >
                        {item.headline}
                    </Text>
                )}
            </View>
            <View style={{ marginTop: 6 }}>
                <Text style={{ fontSize: nd.dateSize, fontFamily: PF.mono, color: c(nd.dateColor) }}>{item.date}</Text>
            </View>
        </View>
    );
};

const PdfSectorMetric: React.FC<{ m: SectorMetric }> = ({ m }) => (
    <View
        style={{
            flex: 1,
            backgroundColor: '#FFFFFF',
            borderWidth: nd.cardBorder,
            borderColor: overviewColors.border,
            borderRadius: nd.cardRadius,
            paddingHorizontal: nd.sectorMetricPad,
            paddingVertical: 6,
            minHeight: 60,
        }}
        wrap={false}
    >
        <Text
            style={{
                fontSize: nd.sectorMetricLabelSize,
                fontFamily: PF.sans,
                fontWeight: 600,
                color: c(nd.sectorMetricLabelColor),
                letterSpacing: nd.sectorMetricLabelTrackingEm * nd.sectorMetricLabelSize,
                textTransform: 'uppercase',
            }}
        >
            {m.label}
        </Text>
        <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 2, marginTop: 3 }}>
            <Text
                style={{
                    fontSize: nd.sectorMetricValueSize,
                    fontFamily: PF.mono,
                    fontWeight: 500,
                    color: m.valueColor ?? c(nd.sectorMetricValueColor),
                }}
            >
                {m.value}
            </Text>
            {m.unit ? (
                <Text style={{ fontSize: nd.sectorMetricUnitSize, fontFamily: PF.mono, color: c(nd.sectorMetricUnitColor) }}>{m.unit}</Text>
            ) : null}
        </View>
        {/* Sub text ("FY24, convenience foods", "by 2028", etc.). The card is a
            flex column with `minHeight: 60`; the flex:1 spacer above pushes the
            sub to the bottom of that reserved space, and `textAlign: 'right'`
            anchors it to the right edge — quieter than sitting flush under the
            value. Smaller font (sectorMetricSubSize - 1pt) reduces visual weight. */}
        {m.sub ? (
            <>
                <View style={{ flex: 1 }} />
                <Text
                    style={{
                        fontSize: nd.sectorMetricSubSize - 1,
                        fontFamily: PF.sans,
                        color: c(nd.sectorMetricSubColor),
                        textAlign: 'right',
                        marginTop: 4,
                    }}
                >
                    {m.sub}
                </Text>
            </>
        ) : null}
    </View>
);

const PdfDriverList: React.FC<{ items: string[]; header: string; tone: 'green' | 'red' }> = ({ items, header, tone }) => {
    const headerColor = tone === 'green' ? c(nd.drGreenColor) : c(nd.drRedColor);
    return (
        <View
            style={{
                flex: 1,
                backgroundColor: c(nd.drCardBg),
                borderRadius: nd.drCardRadius,
                padding: nd.drCardPad,
            }}
        >
            <Text
                style={{
                    fontSize: nd.drHeaderSize,
                    fontFamily: PF.sans,
                    fontWeight: 500,
                    color: headerColor,
                    letterSpacing: nd.drHeaderTrackingEm * nd.drHeaderSize,
                    textTransform: 'uppercase',
                    marginBottom: 5,
                }}
            >
                {header}
            </Text>
            {items.map((item, i) => (
                <View
                    key={i}
                    style={{
                        flexDirection: 'row',
                        gap: 7,
                        paddingVertical: nd.drListItemPadY,
                        borderBottomWidth: i < items.length - 1 ? nd.drListBorder : 0,
                        borderBottomColor: c(nd.drListBorderColor),
                    }}
                >
                    <Text style={{ fontSize: nd.drDashSize, fontFamily: PF.display, color: c(nd.drDashColor), lineHeight: 1 }}>—</Text>
                    <Text style={{ flex: 1, fontSize: nd.drItemSize, fontFamily: PF.sans, color: c(nd.drItemColor), lineHeight: 1.5 }}>
                        {item}
                    </Text>
                </View>
            ))}
        </View>
    );
};

// ── Key Competitors: 2×2 cards layout (Figma 131:85949) ─────────
//
// Two columns, two rows. Subject company sits in the top-left with a teal
// left border and a tinted background; competitors are plain white cards.
// Each card: name (capped at 2 lines via minHeight) + kicker, description
// (capped at 4 lines via minHeight per skill rule 3), divider, then a
// "Founded" + "Location" footer row.
//
// Why 2×2 instead of 4-up: a single row of 4 cards across A4 gives each
// card ~115pt of width — name + description for entities like "Arya.ag
// (Arya Collateral Warehousing Services Private Limited)" overflow that
// budget badly. 2 columns gives each card ~245pt — comfortable for typical
// entity names without ballooning vertically.
//
// Width is driven by `flex: 1` siblings — never `flexWrap` (skill rule 2).

const pairsOfCards = <T,>(arr: T[]): Array<[T | undefined, T | undefined]> => {
    const out: Array<[T | undefined, T | undefined]> = [];
    for (let i = 0; i < arr.length; i += 2) out.push([arr[i], arr[i + 1]]);
    return out;
};

const PdfCompetitorsCards: React.FC<{ vm: NonNullable<OverviewVM['competitors']> }> = ({ vm }) => {
    const cards = vm.cards;
    if (cards.length === 0) return null;
    const nameMinHeight = cd.cardNameSize * cd.cardNameLineHeight * cd.cardNameMaxLines;
    const descMinHeight = cd.cardDescSize * cd.cardDescLineHeight * cd.cardDescMaxLines;
    const rows = pairsOfCards(cards);
    return (
        <View style={{ flexDirection: 'column', gap: cd.cardGap }}>
            {rows.map((row, ri) => (
                <View key={ri} style={{ flexDirection: 'row', gap: cd.cardGap }} wrap={false}>
                    {row.map((card, ci) => {
                        if (!card) return <View key={ci} style={{ flex: 1 }} />;
                        const subject = card.kind === 'subject';
                        const kickerLabel = subject ? 'Subject Company' : 'Competitor';
                        const kickerColor = subject ? c(cd.cardKickerSubjectColor) : c(cd.cardKickerColor);
                        return (
                            <View
                                key={ci}
                                wrap={false}
                                style={{
                                    flex: 1,
                                    backgroundColor: subject ? c(cd.cardSubjectBg) : '#FFFFFF',
                                    borderTopWidth: cd.cardBorder,
                                    borderRightWidth: cd.cardBorder,
                                    borderBottomWidth: cd.cardBorder,
                                    borderLeftWidth: subject ? cd.cardSubjectBorderLeft : cd.cardBorder,
                                    borderTopColor: c(cd.cardBorderColor),
                                    borderRightColor: c(cd.cardBorderColor),
                                    borderBottomColor: c(cd.cardBorderColor),
                                    borderLeftColor: subject ? c(cd.cardSubjectBorderColor) : c(cd.cardBorderColor),
                                    borderRadius: cd.cardRadius,
                                    padding: cd.cardPad,
                                    paddingLeft: subject ? cd.cardPad + cd.cardSubjectBorderLeft - cd.cardBorder : cd.cardPad,
                                }}
                            >
                                <View style={{ minHeight: nameMinHeight }}>
                                    <Text
                                        style={{
                                            fontSize: cd.cardNameSize,
                                            fontFamily: PF.sans,
                                            fontWeight: 600,
                                            color: c(cd.cardNameColor),
                                            lineHeight: cd.cardNameLineHeight,
                                        }}
                                    >
                                        {card.name}
                                    </Text>
                                </View>
                                <Text
                                    style={{
                                        fontSize: cd.cardKickerSize,
                                        fontFamily: PF.sans,
                                        fontWeight: 500,
                                        color: kickerColor,
                                        letterSpacing: cd.cardKickerTrackingEm * cd.cardKickerSize,
                                        textTransform: 'uppercase',
                                        marginTop: cd.cardKickerGap,
                                    }}
                                >
                                    {kickerLabel}
                                </Text>
                                <View style={{ marginTop: cd.cardDescGap, minHeight: descMinHeight }}>
                                    {card.description ? (
                                        <Text
                                            style={{
                                                fontSize: cd.cardDescSize,
                                                fontFamily: PF.sans,
                                                color: c(cd.cardDescColor),
                                                lineHeight: cd.cardDescLineHeight,
                                            }}
                                        >
                                            {card.description}
                                        </Text>
                                    ) : null}
                                </View>
                                <View
                                    style={{
                                        borderTopWidth: cd.cardBorder,
                                        borderTopColor: cd.cardDividerColor,
                                        marginTop: 6,
                                    }}
                                >
                                    <View
                                        style={{
                                            flexDirection: 'row',
                                            justifyContent: 'space-between',
                                            paddingTop: cd.cardRowPadY,
                                            paddingBottom: cd.cardRowPadY,
                                            borderBottomWidth: cd.cardBorder,
                                            borderBottomColor: cd.cardDividerColor,
                                        }}
                                    >
                                        <Text style={{ fontSize: cd.cardRowSize, fontFamily: PF.sans, color: c(cd.cardRowLabelColor) }}>
                                            Founded
                                        </Text>
                                        <Text
                                            style={{
                                                fontSize: cd.cardRowSize,
                                                fontFamily: PF.mono,
                                                fontWeight: 500,
                                                color: c(cd.cardRowValueColor),
                                            }}
                                        >
                                            {card.foundingYear ?? '—'}
                                        </Text>
                                    </View>
                                    <View
                                        style={{
                                            flexDirection: 'row',
                                            justifyContent: 'space-between',
                                            paddingTop: cd.cardRowPadY,
                                            paddingBottom: cd.cardRowPadY,
                                        }}
                                    >
                                        <Text style={{ fontSize: cd.cardRowSize, fontFamily: PF.sans, color: c(cd.cardRowLabelColor) }}>
                                            Location
                                        </Text>
                                        <Text
                                            style={{
                                                fontSize: cd.cardRowSize,
                                                fontFamily: PF.mono,
                                                fontWeight: 500,
                                                color: c(cd.cardRowValueColor),
                                            }}
                                        >
                                            {card.location ?? '—'}
                                        </Text>
                                    </View>
                                </View>
                            </View>
                        );
                    })}
                </View>
            ))}
        </View>
    );
};

// Legacy comparison table — kept in source so we can swap back via the
// render line below if the new card layout doesn't fit a future spec.
const PdfCompetitorsTable: React.FC<{ vm: NonNullable<OverviewVM['competitors']> }> = ({ vm }) => {
    const cols = [vm.focal.name, ...vm.rivals.map((r) => r.name)];
    return (
        <View
            style={{
                borderTopWidth: cd.headerBorder,
                borderTopColor: c(cd.headerBorderColor),
                borderBottomWidth: cd.headerBorder,
                borderBottomColor: c(cd.headerBorderColor),
            }}
        >
            {/* Header row */}
            <View style={{ flexDirection: 'row' }} wrap={false}>
                <View style={{ width: cd.labelColWidth }} />
                {cols.map((name, i) => {
                    const focal = i === 0;
                    return (
                        <View
                            key={i}
                            style={{
                                flex: 1,
                                backgroundColor: focal ? c(cd.focalHeaderBg) : '#FFFFFF',
                                borderBottomWidth: cd.headerBorder,
                                borderBottomColor: focal ? c(cd.focalHeaderBorder) : c(cd.headerBorderColor),
                                paddingHorizontal: cd.cellPadX,
                                paddingVertical: cd.cellPadY + 4,
                            }}
                        >
                            <Text
                                style={{
                                    fontSize: cd.headerSize,
                                    fontFamily: PF.sans,
                                    fontWeight: 600,
                                    color: focal ? c(cd.headerFocalColor) : c(cd.headerColor),
                                    letterSpacing: cd.headerTrackingEm * cd.headerSize,
                                    textTransform: 'uppercase',
                                }}
                            >
                                {name}
                            </Text>
                        </View>
                    );
                })}
            </View>
            {/* Body rows */}
            {vm.rows.map((row) => (
                <View key={row.key} style={{ flexDirection: 'row' }} wrap={false}>
                    <View
                        style={{
                            width: cd.labelColWidth,
                            paddingHorizontal: cd.cellPadX,
                            paddingVertical: cd.cellPadY,
                            borderBottomWidth: cd.border,
                            borderBottomColor: c(cd.borderColor),
                        }}
                    >
                        <Text
                            style={{
                                fontSize: cd.labelSize,
                                fontFamily: PF.sans,
                                fontWeight: 500,
                                color: c(cd.labelColor),
                                letterSpacing: cd.labelTrackingEm * cd.labelSize,
                                textTransform: 'uppercase',
                            }}
                        >
                            {row.label}
                        </Text>
                    </View>
                    {row.values.map((value, i) => {
                        const focal = i === 0;
                        const muted = value === 'Not disclosed';
                        return (
                            <View
                                key={i}
                                style={{
                                    flex: 1,
                                    backgroundColor: focal ? c(cd.focalBg) : '#FFFFFF',
                                    paddingHorizontal: cd.cellPadX,
                                    paddingVertical: cd.cellPadY,
                                    borderBottomWidth: cd.border,
                                    borderBottomColor: c(cd.borderColor),
                                }}
                            >
                                <Text
                                    style={{
                                        fontSize: cd.cellSize,
                                        fontFamily: row.mono ? PF.mono : PF.sans,
                                        color: muted ? c(cd.cellMutedColor) : c(cd.cellColor),
                                        lineHeight: cd.cellLineHeight,
                                    }}
                                >
                                    {value}
                                </Text>
                            </View>
                        );
                    })}
                </View>
            ))}
        </View>
    );
};

const PdfOverview: React.FC<{ data: ReportData; index: string; breakBefore?: boolean }> = ({ data, index, breakBefore }) => {
    const o = data.overview;
    if (!o) return null;
    const vm = buildVM(data);

    return (
        <PdfSection kicker="Section 01" title="Overview" index={index} breakBefore={breakBefore}>
            <PdfIdentityCard vm={vm} />

            {vm.aboutText ? (
                <>
                    <PdfH3>About the Company</PdfH3>
                    <Text
                        style={{
                            fontSize: od.bodySize,
                            fontFamily: PF.sans,
                            color: c(od.bodyColor),
                            lineHeight: od.bodyLineHeight,
                        }}
                    >
                        {vm.aboutText}
                    </Text>
                </>
            ) : null}

            {vm.businessCards.length > 0 ? (
                <>
                    <PdfH3>Business Profile</PdfH3>
                    <View style={{ flexDirection: 'row', gap: od.bpGap }}>
                        {vm.businessCards.map((card) => (
                            <PdfBusinessCard key={card.title} title={card.title} body={card.body} />
                        ))}
                    </View>
                </>
            ) : null}

            {vm.basicDetails.items.length > 0 ? (
                <>
                    <PdfH3>Basic Details</PdfH3>
                    <PdfBasicDetails vm={vm.basicDetails} />
                </>
            ) : null}

            {vm.riskGrade ? (
                <>
                    <PdfH3>Risk Grade</PdfH3>
                    <PdfRiskGrade vm={vm.riskGrade} />
                </>
            ) : null}

            {vm.news.length > 0 ? (
                <View style={{ marginTop: interSectionGap }} minPresenceAhead={140}>
                    <PdfH3 topGap={0}>News &amp; Updates</PdfH3>
                    {/* 2x2 grid built as explicit rows of 2. flex-wrap was unreliable in
                        react-pdf — heights diverged and cards overlapped. Explicit rows
                        with flex:1 children give a stable 2-up layout. */}
                    {[
                        [vm.news[0], vm.news[1]],
                        [vm.news[2], vm.news[3]],
                    ]
                        .filter((row) => row[0] || row[1])
                        .map((row, ri) => (
                            <View
                                key={ri}
                                style={{ flexDirection: 'row', gap: nd.gridGap, marginTop: ri === 0 ? 0 : nd.gridGap }}
                                wrap={false}
                            >
                                {row.map((n, ci) =>
                                    n ? (
                                        <View key={ci} style={{ flex: 1 }} wrap={false}>
                                            <PdfNewsCard item={n} />
                                        </View>
                                    ) : (
                                        <View key={ci} style={{ flex: 1 }} />
                                    ),
                                )}
                            </View>
                        ))}
                </View>
            ) : null}

            {vm.sectorOutlook ? (
                <View style={{ marginTop: interSectionGap }} minPresenceAhead={120}>
                    <View
                        style={{
                            flexDirection: 'row',
                            alignItems: 'baseline',
                            gap: 6,
                            marginBottom: od.headingToBody,
                        }}
                    >
                        <Text style={{ fontSize: od.h3Size, fontFamily: PF.sans, fontWeight: 600, color: c(od.h3Color) }}>
                            Sector / Industry Outlook
                        </Text>
                        {vm.sectorOutlook.sectorName ? (
                            <Text style={{ fontSize: 7.5, fontFamily: PF.sans, color: overviewColors.inkMuted }}>
                                {vm.sectorOutlook.sectorName}
                            </Text>
                        ) : null}
                    </View>
                    {vm.sectorOutlook.metrics.length > 0 ? (
                        <View style={{ flexDirection: 'row', gap: nd.gridGap, marginBottom: nd.sectorGap }}>
                            {vm.sectorOutlook.metrics.map((m) => (
                                <PdfSectorMetric key={m.label} m={m} />
                            ))}
                        </View>
                    ) : null}
                    {vm.sectorOutlook.body ? (
                        <Text
                            style={{
                                fontSize: nd.sectorBodySize,
                                fontFamily: PF.sans,
                                color: c(nd.sectorBodyColor),
                                lineHeight: nd.sectorBodyLineHeight,
                                marginBottom: nd.sectorGap,
                            }}
                        >
                            {vm.sectorOutlook.body}
                        </Text>
                    ) : null}
                    {vm.sectorOutlook.growthDrivers.length > 0 || vm.sectorOutlook.keyRisks.length > 0 ? (
                        <View style={{ flexDirection: 'row', gap: nd.gridGap }}>
                            {vm.sectorOutlook.growthDrivers.length > 0 ? (
                                <PdfDriverList items={vm.sectorOutlook.growthDrivers} header="Growth Drivers" tone="green" />
                            ) : null}
                            {vm.sectorOutlook.keyRisks.length > 0 ? (
                                <PdfDriverList items={vm.sectorOutlook.keyRisks} header="Key Risks" tone="red" />
                            ) : null}
                        </View>
                    ) : null}
                </View>
            ) : null}

            {vm.competitors ? (
                <View style={{ marginTop: interSectionGap }} minPresenceAhead={210}>
                    <PdfH3 topGap={0}>Key Competitors</PdfH3>
                    <PdfCompetitorsCards vm={vm.competitors} />
                </View>
            ) : null}
            <PdfOverviewMiniCharts data={data} />
        </PdfSection>
    );
};

// Employee headcount + Website traffic charts. Stacked full-width — line chart
// for headcount on top, sparkbar for traffic below. Both data sources live on
// ReportData (`management.momEmployee` and `identity.websiteTraffic`).
const buildEmployeePoints = (data: ReportData): LineChartPoint[] => {
    const mom = data.management?.momEmployee ?? [];
    return mom.slice(-18).map((x) => ({ label: fmtMonthYearShort(x.yearMonth), value: x.count }));
};
const buildTrafficValues = (data: ReportData): number[] => {
    const monthly = data.identity?.websiteTraffic?.monthly ?? [];
    return monthly.slice(-18).map((m) => m.traffic);
};
// PDF body width at A4 (595pt) minus the section padding (~36pt × 2). Keep
// chart widths just under that so the chart fills the line without bleeding
// into the section borders.
const CHART_FULL_WIDTH = 520;

const PdfOverviewMiniCharts: React.FC<{ data: ReportData }> = ({ data }) => {
    const emp = buildEmployeePoints(data);
    const traffic = buildTrafficValues(data);
    const showEmp = emp.length >= 2;
    const showTraffic = traffic.length >= 2;
    if (!showEmp && !showTraffic) return null;
    const trafficChange = data.identity?.websiteTraffic?.change;
    return (
        <View style={{ marginTop: interSectionGap }} minPresenceAhead={160}>
            {showEmp ? (
                <View wrap={false}>
                    <PdfH3 topGap={0}>Employee Headcount</PdfH3>
                    <PdfLineChart points={emp} height={120} width={CHART_FULL_WIDTH} />
                    <Text style={{ fontSize: 6.5, color: overviewColors.inkMuted, marginTop: 4 }}>
                        Monthly EPF-registered employees · last 18 months
                    </Text>
                </View>
            ) : null}
            {showTraffic ? (
                <View wrap={false} style={{ marginTop: showEmp ? 14 : 0 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 6 }}>
                        <PdfH3 topGap={0}>Website Traffic</PdfH3>
                        {trafficChange ? (
                            <Text style={{ fontSize: 7, color: overviewColors.inkMuted, fontFamily: PF.mono }}>{trafficChange}</Text>
                        ) : null}
                    </View>
                    <PdfSparkBars values={traffic} height={64} />
                    <Text style={{ fontSize: 6.5, color: overviewColors.inkMuted, marginTop: 4 }}>
                        Monthly unique visits · source: SimilarWeb
                    </Text>
                </View>
            ) : null}
        </View>
    );
};

// ════════════════════════════════════════════════════════════════
//                          S C R E E N
// ════════════════════════════════════════════════════════════════

const SF = coverFonts.screen;

const ScreenH3: React.FC<{ children: React.ReactNode; topGap?: number }> = ({ children, topGap = od.sectionGap }) => (
    <div
        style={{
            fontSize: ptToPx(od.h3Size),
            fontFamily: SF.sans,
            fontWeight: 600,
            color: c(od.h3Color),
            marginTop: ptToPx(topGap),
            marginBottom: ptToPx(od.headingToBody),
        }}
    >
        {children}
    </div>
);

const ScreenChipBox: React.FC<{ label: string; tone: ChipTone }> = ({ label, tone }) => {
    const tones: Record<ChipTone, { bg: string; fg: string }> = {
        positive: { bg: overviewColors.posSoft, fg: overviewColors.pos },
        accent: { bg: overviewColors.accentSoft, fg: overviewColors.accentDeep },
        muted: { bg: overviewColors.surfaceAlt, fg: overviewColors.inkSubtle },
    };
    const t = tones[tone];
    return (
        <span
            style={{
                background: t.bg,
                paddingLeft: ptToPx(od.idChipPadX),
                paddingRight: ptToPx(od.idChipPadX),
                paddingTop: ptToPx(od.idChipPadY),
                paddingBottom: ptToPx(od.idChipPadY + 1),
                borderRadius: ptToPx(od.idChipRadius),
                fontSize: ptToPx(od.idChipSize),
                fontFamily: SF.sans,
                fontWeight: 600,
                color: t.fg,
                letterSpacing: `${od.idChipTrackingEm}em`,
                textTransform: 'uppercase',
            }}
        >
            {label}
        </span>
    );
};

const ScreenIdentityCard: React.FC<{ vm: OverviewVM }> = ({ vm }) => (
    <div
        style={{
            background: overviewColors.surface,
            borderRadius: ptToPx(od.idCardRadius),
            paddingLeft: ptToPx(od.idCardPadX),
            paddingRight: ptToPx(od.idCardPadX),
            paddingTop: ptToPx(od.idCardPadY),
            paddingBottom: ptToPx(od.idCardPadY),
            display: 'flex',
            alignItems: 'center',
            gap: ptToPx(od.idCardGap),
        }}
    >
        <div
            style={{
                width: ptToPx(od.idLogoSize),
                height: ptToPx(od.idLogoSize),
                borderRadius: ptToPx(od.idLogoRadius),
                background: overviewColors.ink,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            }}
        >
            <span style={{ fontSize: ptToPx(od.idLogoTextSize), color: '#FFFFFF', fontFamily: SF.display, lineHeight: 1 }}>
                {vm.initial}
            </span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: ptToPx(od.idChipGap), flexWrap: 'wrap' }}>
                <span
                    style={{
                        fontSize: ptToPx(od.idTitleSize),
                        fontFamily: SF.display,
                        color: overviewColors.ink,
                        lineHeight: od.idTitleLineHeight,
                    }}
                >
                    {vm.orgTitle}
                </span>
                {vm.chips.map((ch) => (
                    <ScreenChipBox key={ch.label} label={ch.label} tone={ch.tone} />
                ))}
            </div>
            {vm.subtitle ? (
                <div
                    style={{
                        fontSize: ptToPx(od.idSubtitleSize),
                        fontFamily: SF.sans,
                        color: c(od.idSubtitleColor),
                        marginTop: 4,
                        lineHeight: od.idSubtitleLineHeight,
                    }}
                >
                    {vm.subtitle}
                </div>
            ) : null}
        </div>
    </div>
);

const ScreenBusinessCard: React.FC<{ title: string; body: string }> = ({ title, body }) => (
    <div
        style={{
            flex: 1,
            background: '#FFFFFF',
            border: `${ptToPx(od.bpCardBorder)}px solid ${overviewColors.border}`,
            borderRadius: ptToPx(od.bpCardRadius),
            padding: ptToPx(od.bpCardPad),
        }}
    >
        <div
            style={{
                fontSize: ptToPx(od.bpTitleSize),
                fontFamily: SF.sans,
                fontWeight: 600,
                color: c(od.bpTitleColor),
                marginBottom: 6,
            }}
        >
            {title}
        </div>
        <div
            style={{
                fontSize: ptToPx(od.bpBodySize),
                fontFamily: SF.sans,
                color: c(od.bpBodyColor),
                lineHeight: od.bpBodyLineHeight,
            }}
        >
            {body}
        </div>
    </div>
);

const ScreenBasicDetails: React.FC<{ vm: OverviewVM['basicDetails'] }> = ({ vm }) => (
    <div
        style={{
            background: '#FFFFFF',
            border: `${ptToPx(od.bdCardBorder)}px solid ${overviewColors.border}`,
            borderRadius: ptToPx(od.bdCardRadius),
            padding: ptToPx(od.bdCardPad),
        }}
    >
        <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: ptToPx(od.bdItemGap), rowGap: 5 }}>
            {vm.items.map((it) => (
                <div key={it.label} style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
                    <span
                        style={{
                            fontSize: ptToPx(od.bdLabelSize),
                            fontFamily: SF.sans,
                            fontWeight: 500,
                            color: c(od.bdLabelColor),
                            letterSpacing: `${od.bdLabelTrackingEm}em`,
                            textTransform: 'uppercase',
                        }}
                    >
                        {it.label}:
                    </span>
                    {it.href ? (
                        <a
                            href={it.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                fontSize: ptToPx(od.bdValueSize),
                                fontFamily: SF.mono,
                                fontWeight: 500,
                                color: overviewColors.accent,
                                textDecoration: 'none',
                            }}
                        >
                            {it.value}
                        </a>
                    ) : (
                        <span style={{ fontSize: ptToPx(od.bdValueSize), fontFamily: SF.mono, fontWeight: 500, color: c(od.bdValueColor) }}>
                            {it.value}
                        </span>
                    )}
                </div>
            ))}
        </div>
        {vm.address ? (
            <div
                style={{
                    fontSize: ptToPx(od.bdAddressSize),
                    fontFamily: SF.sans,
                    color: c(od.bdAddressColor),
                    marginTop: ptToPx(od.bdRowGap),
                    lineHeight: od.bdAddressLineHeight,
                }}
            >
                {vm.address}
            </div>
        ) : null}
    </div>
);

const ScreenRiskGrade: React.FC<{ vm: NonNullable<OverviewVM['riskGrade']> }> = ({ vm }) => (
    <div style={{ display: 'flex', gap: ptToPx(od.bpGap) }}>
        <div
            style={{
                flex: 1,
                background: '#FFFFFF',
                border: `${ptToPx(od.rgCardBorder)}px solid ${overviewColors.border}`,
                borderRadius: ptToPx(od.rgCardRadius),
                padding: ptToPx(od.rgCardPad),
                display: 'flex',
                alignItems: 'center',
                gap: ptToPx(od.rgGap),
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div
                    style={{
                        fontSize: ptToPx(od.rgBadgeSize),
                        fontFamily: SF.mono,
                        fontWeight: 500,
                        color: vm.color,
                        letterSpacing: `${od.rgBadgeTrackingEm}em`,
                        lineHeight: 1.1,
                        marginBottom: 8,
                    }}
                >
                    {bandLabel(vm.band)}
                </div>
                <div
                    style={{
                        width: ptToPx(od.rgBarWidth),
                        height: ptToPx(od.rgBarHeight),
                        background: overviewColors.surfaceAlt,
                        borderRadius: ptToPx(od.rgBarRadius),
                        overflow: 'hidden',
                    }}
                >
                    <div
                        style={{
                            width: `${vm.fraction * 100}%`,
                            height: '100%',
                            background: vm.color,
                            borderRadius: ptToPx(od.rgBarRadius),
                        }}
                    />
                </div>
            </div>
            <div
                style={{
                    flex: 1,
                    fontSize: ptToPx(od.rgDescSize),
                    fontFamily: SF.sans,
                    color: c(od.rgDescColor),
                    lineHeight: od.rgDescLineHeight,
                }}
            >
                {vm.description}
            </div>
        </div>
        {vm.pillars.length > 0 ? (
            <div
                style={{
                    flex: 1,
                    background: '#FFFFFF',
                    border: `${ptToPx(od.rgCardBorder)}px solid ${overviewColors.border}`,
                    borderRadius: ptToPx(od.rgCardRadius),
                    paddingLeft: ptToPx(od.rgCardPad),
                    paddingRight: ptToPx(od.rgCardPad),
                    paddingTop: ptToPx(od.rgCardPad - 4),
                    paddingBottom: ptToPx(od.rgCardPad - 4),
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                }}
            >
                {vm.pillars.map((p) => (
                    <div
                        key={p.label}
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            minHeight: ptToPx(od.rgPillarRowHeight),
                        }}
                    >
                        <span style={{ fontSize: ptToPx(od.rgPillarLabelSize), color: c(od.rgPillarLabelColor), fontFamily: SF.sans }}>
                            {p.label}
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span
                                style={{
                                    width: ptToPx(od.rgPillarDotSize),
                                    height: ptToPx(od.rgPillarDotSize),
                                    borderRadius: '50%',
                                    background: p.color,
                                    display: 'inline-block',
                                }}
                            />
                            <span
                                style={{
                                    fontSize: ptToPx(od.rgPillarBandSize),
                                    fontFamily: SF.sans,
                                    fontWeight: 600,
                                    color: p.color,
                                    letterSpacing: '0.06em',
                                    textTransform: 'uppercase',
                                }}
                            >
                                {bandLabel(p.band)}
                            </span>
                        </span>
                    </div>
                ))}
            </div>
        ) : null}
    </div>
);

const ScreenNewsCard: React.FC<{ item: OverviewVM['news'][number] }> = ({ item }) => {
    const sentColors = {
        pos: { bg: overviewColors.posSoft, fg: overviewColors.pos },
        neg: { bg: overviewColors.negSoft, fg: overviewColors.neg },
        neutral: { bg: overviewColors.surfaceAlt, fg: overviewColors.inkSubtle },
    }[item.sentiment];
    return (
        <div
            style={{
                flex: 1,
                background: '#FFFFFF',
                border: `${ptToPx(nd.cardBorder)}px solid ${overviewColors.border}`,
                borderRadius: ptToPx(nd.cardRadius),
                padding: ptToPx(nd.cardPad),
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: ptToPx(nd.sourceGap) }}>
                    <span
                        style={{
                            width: ptToPx(nd.sourcePillSize),
                            height: ptToPx(nd.sourcePillSize),
                            borderRadius: '50%',
                            background: c(nd.sourcePillBg),
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: ptToPx(nd.sourcePillTextSize),
                            color: c(nd.sourcePillTextColor),
                            fontFamily: SF.sans,
                            fontWeight: 600,
                        }}
                    >
                        {item.initials}
                    </span>
                    <span
                        style={{
                            fontSize: ptToPx(nd.sourceLabelSize),
                            color: c(nd.sourceLabelColor),
                            fontFamily: SF.sans,
                            fontWeight: 600,
                        }}
                    >
                        {item.source}
                    </span>
                </div>
                <span
                    style={{
                        background: sentColors.bg,
                        paddingLeft: ptToPx(nd.sentChipPadX),
                        paddingRight: ptToPx(nd.sentChipPadX),
                        paddingTop: ptToPx(nd.sentChipPadY),
                        paddingBottom: ptToPx(nd.sentChipPadY + 1),
                        borderRadius: ptToPx(nd.sentChipRadius),
                        fontSize: ptToPx(nd.sentChipSize),
                        fontFamily: SF.sans,
                        fontWeight: 600,
                        color: sentColors.fg,
                        letterSpacing: `${nd.sentChipTrackingEm}em`,
                        textTransform: 'uppercase',
                    }}
                >
                    {item.sentimentLabel}
                </span>
            </div>
            <div
                style={{
                    marginTop: 5,
                    fontSize: ptToPx(nd.headlineSize),
                    fontFamily: SF.sans,
                    fontWeight: 500,
                    color: c(nd.headlineColor),
                    lineHeight: nd.headlineLineHeight,
                }}
            >
                {item.url ? (
                    <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ color: '#6021B3', textDecoration: 'underline' }}>
                        {item.headline}
                    </a>
                ) : (
                    item.headline
                )}
            </div>
            <div style={{ marginTop: 4, fontSize: ptToPx(nd.dateSize), fontFamily: SF.mono, color: c(nd.dateColor) }}>{item.date}</div>
        </div>
    );
};

const ScreenSectorMetric: React.FC<{ m: SectorMetric }> = ({ m }) => (
    <div
        style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            background: '#FFFFFF',
            border: `${ptToPx(nd.cardBorder)}px solid ${overviewColors.border}`,
            borderRadius: ptToPx(nd.cardRadius),
            paddingLeft: ptToPx(nd.sectorMetricPad),
            paddingRight: ptToPx(nd.sectorMetricPad),
            paddingTop: 8,
            paddingBottom: 8,
            minHeight: ptToPx(60),
        }}
    >
        <div
            style={{
                fontSize: ptToPx(nd.sectorMetricLabelSize),
                fontFamily: SF.sans,
                fontWeight: 600,
                color: c(nd.sectorMetricLabelColor),
                letterSpacing: `${nd.sectorMetricLabelTrackingEm}em`,
                textTransform: 'uppercase',
            }}
        >
            {m.label}
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 3, marginTop: 4 }}>
            <span
                style={{
                    fontSize: ptToPx(nd.sectorMetricValueSize),
                    fontFamily: SF.mono,
                    fontWeight: 500,
                    color: m.valueColor ?? c(nd.sectorMetricValueColor),
                }}
            >
                {m.value}
            </span>
            {m.unit ? (
                <span style={{ fontSize: ptToPx(nd.sectorMetricUnitSize), fontFamily: SF.mono, color: c(nd.sectorMetricUnitColor) }}>
                    {m.unit}
                </span>
            ) : null}
        </div>
        {m.sub ? (
            <div
                style={{
                    fontSize: ptToPx(nd.sectorMetricSubSize - 1),
                    fontFamily: SF.sans,
                    color: c(nd.sectorMetricSubColor),
                    marginTop: 'auto',
                    paddingTop: 4,
                    textAlign: 'right',
                }}
            >
                {m.sub}
            </div>
        ) : null}
    </div>
);

const ScreenDriverList: React.FC<{ items: string[]; header: string; tone: 'green' | 'red' }> = ({ items, header, tone }) => {
    const headerColor = tone === 'green' ? c(nd.drGreenColor) : c(nd.drRedColor);
    return (
        <div
            style={{
                flex: 1,
                background: c(nd.drCardBg),
                borderRadius: ptToPx(nd.drCardRadius),
                padding: ptToPx(nd.drCardPad),
            }}
        >
            <div
                style={{
                    fontSize: ptToPx(nd.drHeaderSize),
                    fontFamily: SF.sans,
                    fontWeight: 500,
                    color: headerColor,
                    letterSpacing: `${nd.drHeaderTrackingEm}em`,
                    textTransform: 'uppercase',
                    marginBottom: 6,
                }}
            >
                {header}
            </div>
            {items.map((item, i) => (
                <div
                    key={i}
                    style={{
                        display: 'flex',
                        gap: 9,
                        paddingTop: ptToPx(nd.drListItemPadY),
                        paddingBottom: ptToPx(nd.drListItemPadY),
                        borderBottom: i < items.length - 1 ? `${ptToPx(nd.drListBorder)}px solid ${c(nd.drListBorderColor)}` : 'none',
                    }}
                >
                    <span style={{ fontSize: ptToPx(nd.drDashSize), fontFamily: SF.display, color: c(nd.drDashColor), lineHeight: 1 }}>
                        —
                    </span>
                    <span
                        style={{ flex: 1, fontSize: ptToPx(nd.drItemSize), fontFamily: SF.sans, color: c(nd.drItemColor), lineHeight: 1.5 }}
                    >
                        {item}
                    </span>
                </div>
            ))}
        </div>
    );
};

const ScreenCompetitorsCards: React.FC<{ vm: NonNullable<OverviewVM['competitors']> }> = ({ vm }) => {
    const cards = vm.cards;
    if (cards.length === 0) return null;
    const nameMinHeight = ptToPx(cd.cardNameSize * cd.cardNameLineHeight * cd.cardNameMaxLines);
    const descMinHeight = ptToPx(cd.cardDescSize * cd.cardDescLineHeight * cd.cardDescMaxLines);
    const rows = pairsOfCards(cards);
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: ptToPx(cd.cardGap) }}>
            {rows.map((row, ri) => (
                <div key={ri} style={{ display: 'flex', gap: ptToPx(cd.cardGap) }}>
                    {row.map((card, ci) => {
                        if (!card) return <div key={ci} style={{ flex: 1 }} />;
                        const subject = card.kind === 'subject';
                        const kickerLabel = subject ? 'Subject Company' : 'Competitor';
                        const kickerColor = subject ? c(cd.cardKickerSubjectColor) : c(cd.cardKickerColor);
                        const leftBorder = subject ? cd.cardSubjectBorderLeft : cd.cardBorder;
                        return (
                            <div
                                key={ci}
                                style={{
                                    flex: 1,
                                    minWidth: 0,
                                    background: subject ? c(cd.cardSubjectBg) : '#FFFFFF',
                                    borderTop: `${ptToPx(cd.cardBorder)}px solid ${c(cd.cardBorderColor)}`,
                                    borderRight: `${ptToPx(cd.cardBorder)}px solid ${c(cd.cardBorderColor)}`,
                                    borderBottom: `${ptToPx(cd.cardBorder)}px solid ${c(cd.cardBorderColor)}`,
                                    borderLeft: `${ptToPx(leftBorder)}px solid ${
                                        subject ? c(cd.cardSubjectBorderColor) : c(cd.cardBorderColor)
                                    }`,
                                    borderRadius: ptToPx(cd.cardRadius),
                                    padding: ptToPx(cd.cardPad),
                                    paddingLeft: ptToPx(subject ? cd.cardPad + cd.cardSubjectBorderLeft - cd.cardBorder : cd.cardPad),
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <div style={{ minHeight: nameMinHeight }}>
                                    <span
                                        style={{
                                            fontSize: ptToPx(cd.cardNameSize),
                                            fontFamily: SF.sans,
                                            fontWeight: 600,
                                            color: c(cd.cardNameColor),
                                            lineHeight: cd.cardNameLineHeight,
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        {card.name}
                                    </span>
                                </div>
                                <div
                                    style={{
                                        fontSize: ptToPx(cd.cardKickerSize),
                                        fontFamily: SF.sans,
                                        fontWeight: 500,
                                        color: kickerColor,
                                        letterSpacing: `${cd.cardKickerTrackingEm}em`,
                                        textTransform: 'uppercase',
                                        marginTop: ptToPx(cd.cardKickerGap),
                                    }}
                                >
                                    {kickerLabel}
                                </div>
                                <div style={{ marginTop: ptToPx(cd.cardDescGap), minHeight: descMinHeight }}>
                                    {card.description ? (
                                        <span
                                            style={{
                                                fontSize: ptToPx(cd.cardDescSize),
                                                fontFamily: SF.sans,
                                                color: c(cd.cardDescColor),
                                                lineHeight: cd.cardDescLineHeight,
                                                wordBreak: 'break-word',
                                            }}
                                        >
                                            {card.description}
                                        </span>
                                    ) : null}
                                </div>
                                <div
                                    style={{
                                        borderTop: `${ptToPx(cd.cardBorder)}px solid ${cd.cardDividerColor}`,
                                        marginTop: ptToPx(6),
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            paddingTop: ptToPx(cd.cardRowPadY),
                                            paddingBottom: ptToPx(cd.cardRowPadY),
                                            borderBottom: `${ptToPx(cd.cardBorder)}px solid ${cd.cardDividerColor}`,
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontSize: ptToPx(cd.cardRowSize),
                                                fontFamily: SF.sans,
                                                color: c(cd.cardRowLabelColor),
                                            }}
                                        >
                                            Founded
                                        </span>
                                        <span
                                            style={{
                                                fontSize: ptToPx(cd.cardRowSize),
                                                fontFamily: SF.mono,
                                                fontWeight: 500,
                                                color: c(cd.cardRowValueColor),
                                            }}
                                        >
                                            {card.foundingYear ?? '—'}
                                        </span>
                                    </div>
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            paddingTop: ptToPx(cd.cardRowPadY),
                                            paddingBottom: ptToPx(cd.cardRowPadY),
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontSize: ptToPx(cd.cardRowSize),
                                                fontFamily: SF.sans,
                                                color: c(cd.cardRowLabelColor),
                                            }}
                                        >
                                            Location
                                        </span>
                                        <span
                                            style={{
                                                fontSize: ptToPx(cd.cardRowSize),
                                                fontFamily: SF.mono,
                                                fontWeight: 500,
                                                color: c(cd.cardRowValueColor),
                                            }}
                                        >
                                            {card.location ?? '—'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

// Legacy comparison table — kept in source so we can swap back via the
// render line below if the new card layout doesn't fit a future spec.
const ScreenCompetitorsTable: React.FC<{ vm: NonNullable<OverviewVM['competitors']> }> = ({ vm }) => {
    const cols = [vm.focal.name, ...vm.rivals.map((r) => r.name)];
    return (
        <div
            style={{
                borderTop: `${ptToPx(cd.headerBorder)}px solid ${c(cd.headerBorderColor)}`,
                borderBottom: `${ptToPx(cd.headerBorder)}px solid ${c(cd.headerBorderColor)}`,
            }}
        >
            <div style={{ display: 'flex' }}>
                <div style={{ width: ptToPx(cd.labelColWidth) }} />
                {cols.map((name, i) => {
                    const focal = i === 0;
                    return (
                        <div
                            key={i}
                            style={{
                                flex: 1,
                                background: focal ? c(cd.focalHeaderBg) : '#FFFFFF',
                                borderBottom: `${ptToPx(cd.headerBorder)}px solid ${
                                    focal ? c(cd.focalHeaderBorder) : c(cd.headerBorderColor)
                                }`,
                                paddingLeft: ptToPx(cd.cellPadX),
                                paddingRight: ptToPx(cd.cellPadX),
                                paddingTop: ptToPx(cd.cellPadY + 4),
                                paddingBottom: ptToPx(cd.cellPadY + 4),
                            }}
                        >
                            <span
                                style={{
                                    fontSize: ptToPx(cd.headerSize),
                                    fontFamily: SF.sans,
                                    fontWeight: 600,
                                    color: focal ? c(cd.headerFocalColor) : c(cd.headerColor),
                                    letterSpacing: `${cd.headerTrackingEm}em`,
                                    textTransform: 'uppercase',
                                }}
                            >
                                {name}
                            </span>
                        </div>
                    );
                })}
            </div>
            {vm.rows.map((row) => (
                <div key={row.key} style={{ display: 'flex' }}>
                    <div
                        style={{
                            width: ptToPx(cd.labelColWidth),
                            paddingLeft: ptToPx(cd.cellPadX),
                            paddingRight: ptToPx(cd.cellPadX),
                            paddingTop: ptToPx(cd.cellPadY),
                            paddingBottom: ptToPx(cd.cellPadY),
                            borderBottom: `${ptToPx(cd.border)}px solid ${c(cd.borderColor)}`,
                        }}
                    >
                        <span
                            style={{
                                fontSize: ptToPx(cd.labelSize),
                                fontFamily: SF.sans,
                                fontWeight: 500,
                                color: c(cd.labelColor),
                                letterSpacing: `${cd.labelTrackingEm}em`,
                                textTransform: 'uppercase',
                            }}
                        >
                            {row.label}
                        </span>
                    </div>
                    {row.values.map((value, i) => {
                        const focal = i === 0;
                        const muted = value === 'Not disclosed';
                        return (
                            <div
                                key={i}
                                style={{
                                    flex: 1,
                                    background: focal ? c(cd.focalBg) : '#FFFFFF',
                                    paddingLeft: ptToPx(cd.cellPadX),
                                    paddingRight: ptToPx(cd.cellPadX),
                                    paddingTop: ptToPx(cd.cellPadY),
                                    paddingBottom: ptToPx(cd.cellPadY),
                                    borderBottom: `${ptToPx(cd.border)}px solid ${c(cd.borderColor)}`,
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: ptToPx(cd.cellSize),
                                        fontFamily: row.mono ? SF.mono : SF.sans,
                                        color: muted ? c(cd.cellMutedColor) : c(cd.cellColor),
                                        lineHeight: cd.cellLineHeight,
                                    }}
                                >
                                    {value}
                                </span>
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

const ScreenOverview: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    const o = data.overview;
    if (!o) return null;
    const vm = buildVM(data);

    return (
        <ScreenSection kicker="Section 01" title="Overview" index={index}>
            <ScreenIdentityCard vm={vm} />

            {vm.aboutText ? (
                <>
                    <ScreenH3>About the Company</ScreenH3>
                    <div
                        style={{
                            fontSize: ptToPx(od.bodySize),
                            fontFamily: SF.sans,
                            color: c(od.bodyColor),
                            lineHeight: od.bodyLineHeight,
                        }}
                    >
                        {vm.aboutText}
                    </div>
                </>
            ) : null}

            {vm.businessCards.length > 0 ? (
                <>
                    <ScreenH3>Business Profile</ScreenH3>
                    <div style={{ display: 'flex', gap: ptToPx(od.bpGap) }}>
                        {vm.businessCards.map((card) => (
                            <ScreenBusinessCard key={card.title} title={card.title} body={card.body} />
                        ))}
                    </div>
                </>
            ) : null}

            {vm.basicDetails.items.length > 0 ? (
                <>
                    <ScreenH3>Basic Details</ScreenH3>
                    <ScreenBasicDetails vm={vm.basicDetails} />
                </>
            ) : null}

            {vm.riskGrade ? (
                <>
                    <ScreenH3>Risk Grade</ScreenH3>
                    <ScreenRiskGrade vm={vm.riskGrade} />
                </>
            ) : null}

            {vm.news.length > 0 ? (
                <div style={{ marginTop: ptToPx(interSectionGap) }}>
                    <ScreenH3 topGap={0}>News &amp; Updates</ScreenH3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: ptToPx(nd.gridGap) }}>
                        {vm.news.map((n, i) => (
                            <ScreenNewsCard key={i} item={n} />
                        ))}
                    </div>
                </div>
            ) : null}

            {vm.sectorOutlook ? (
                <div style={{ marginTop: ptToPx(interSectionGap) }}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: 8,
                            marginBottom: ptToPx(od.headingToBody),
                        }}
                    >
                        <span style={{ fontSize: ptToPx(od.h3Size), fontFamily: SF.sans, fontWeight: 600, color: c(od.h3Color) }}>
                            Sector / Industry Outlook
                        </span>
                        {vm.sectorOutlook.sectorName ? (
                            <span style={{ fontSize: ptToPx(7.5), fontFamily: SF.sans, color: overviewColors.inkMuted }}>
                                {vm.sectorOutlook.sectorName}
                            </span>
                        ) : null}
                    </div>
                    {vm.sectorOutlook.metrics.length > 0 ? (
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: `repeat(${vm.sectorOutlook.metrics.length}, 1fr)`,
                                gap: ptToPx(nd.gridGap),
                                marginBottom: ptToPx(nd.sectorGap),
                            }}
                        >
                            {vm.sectorOutlook.metrics.map((m) => (
                                <ScreenSectorMetric key={m.label} m={m} />
                            ))}
                        </div>
                    ) : null}
                    {vm.sectorOutlook.body ? (
                        <div
                            style={{
                                fontSize: ptToPx(nd.sectorBodySize),
                                fontFamily: SF.sans,
                                color: c(nd.sectorBodyColor),
                                lineHeight: nd.sectorBodyLineHeight,
                                marginBottom: ptToPx(nd.sectorGap),
                            }}
                        >
                            {vm.sectorOutlook.body}
                        </div>
                    ) : null}
                    {vm.sectorOutlook.growthDrivers.length > 0 || vm.sectorOutlook.keyRisks.length > 0 ? (
                        <div style={{ display: 'flex', gap: ptToPx(nd.gridGap) }}>
                            {vm.sectorOutlook.growthDrivers.length > 0 ? (
                                <ScreenDriverList items={vm.sectorOutlook.growthDrivers} header="Growth Drivers" tone="green" />
                            ) : null}
                            {vm.sectorOutlook.keyRisks.length > 0 ? (
                                <ScreenDriverList items={vm.sectorOutlook.keyRisks} header="Key Risks" tone="red" />
                            ) : null}
                        </div>
                    ) : null}
                </div>
            ) : null}

            {vm.competitors ? (
                <div style={{ marginTop: ptToPx(interSectionGap) }}>
                    <ScreenH3 topGap={0}>Key Competitors</ScreenH3>
                    <ScreenCompetitorsCards vm={vm.competitors} />
                </div>
            ) : null}
            <ScreenOverviewMiniCharts data={data} />
        </ScreenSection>
    );
};

const ScreenOverviewMiniCharts: React.FC<{ data: ReportData }> = ({ data }) => {
    const emp = buildEmployeePoints(data);
    const traffic = buildTrafficValues(data);
    const showEmp = emp.length >= 2;
    const showTraffic = traffic.length >= 2;
    if (!showEmp && !showTraffic) return null;
    const trafficChange = data.identity?.websiteTraffic?.change;
    return (
        <div style={{ marginTop: ptToPx(interSectionGap) }}>
            {showEmp ? (
                <div>
                    <ScreenH3 topGap={0}>Employee Headcount</ScreenH3>
                    <ScreenLineChart points={emp} height={140} />
                    <div style={{ fontSize: '0.7rem', color: overviewColors.inkMuted, marginTop: 4 }}>
                        Monthly EPF-registered employees · last 18 months
                    </div>
                </div>
            ) : null}
            {showTraffic ? (
                <div style={{ marginTop: showEmp ? 18 : 0 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                        <ScreenH3 topGap={0}>Website Traffic</ScreenH3>
                        {trafficChange ? (
                            <span style={{ fontSize: '0.75rem', color: overviewColors.inkMuted, fontFamily: SF.mono }}>
                                {trafficChange}
                            </span>
                        ) : null}
                    </div>
                    <ScreenSparkBars values={traffic} height={80} />
                    <div style={{ fontSize: '0.7rem', color: overviewColors.inkMuted, marginTop: 4 }}>
                        Monthly unique visits · source: SimilarWeb
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export const overviewModule: ModuleDefinition = {
    meta: {
        id: 'overview',
        title: 'Company overview',
        description: 'Company identity, business profile, basic details, risk grade, news, sector outlook and competitors.',
        dataSource: 'Profile Overview, Sector Outlook, News, Competitors',
    },
    hasData: (d) => !!d.overview?.overviewContent,
    Pdf: PdfOverview,
    Screen: ScreenOverview,
};
